import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, audit, catchError, concatMap, filter, from, ignoreElements, race, timer } from 'rxjs';
import { AppStateService } from '../shared/app-state.service';
import { LOGGER } from '../shared/logger';
import { STATE_STORAGE } from './state-storage';

/** Collect changes for this long before writing, so typing does not cause a write per keystroke. */
const WRITE_INTERVAL_MS = 300;

@Injectable({ providedIn: 'root' })
export class StatePersistence {
  private readonly storage = inject(STATE_STORAGE);
  private readonly appState = inject(AppStateService);
  private readonly log = inject(LOGGER).scope('persist');

  /**
   * Writes changes of `state$` under `key`, starting from the state `hydrated` holds.
   *
   * `state$` replays its current value on subscription, and that value is what was just
   * read out of storage. Writing it back would mean a native write on every app start that
   * changes nothing, so the first emission is dropped — but only if it really is that same
   * state. Comparing instead of counting keeps this correct even if something else manages
   * to change the state before this subscribes; then the first emission is a real change
   * and gets written.
   *
   * Only start this once hydration has finished — otherwise the initial state would
   * overwrite the stored settings before they were ever read.
   *
   * `concatMap` rather than `switchMap`: the write is a native call that keeps running
   * even when its observable is unsubscribed, so cancelling could let an older write
   * land after a newer one.
   */
  persist<T>(key: string, state$: Observable<T>, hydrated: T): Observable<never> {
    return state$.pipe(
      filter((state, index) => index > 0 || state !== hydrated),
      // Write at most every WRITE_INTERVAL_MS, but immediately when the app is being
      // backgrounded — a pending write may not survive the OS suspending the webview.
      audit(() => race(timer(WRITE_INTERVAL_MS), this.backgrounded$())),
      concatMap(state =>
        from(this.storage.save(key, state)).pipe(
          catchError((e: unknown) => {
            // A failed write must not tear down the stream, or nothing is ever saved again.
            this.log.error('write failed', { key, error: String(e) });
            return EMPTY;
          }),
        ),
      ),
      ignoreElements(),
    );
  }

  private backgrounded$(): Observable<boolean> {
    return this.appState.isActive$.pipe(filter(active => !active));
  }
}
